import { useMutation } from "@tanstack/react-query";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Badge, Button, Card, Col, Form, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Contact } from "../../../../api/src/contacts/lib";
import type { EditableHousehold } from "../../../../api/src/households-base/households-base.service";
import type { HouseholdsController } from "../../../../api/src/households/households.controller";
import type { SerializedObject, UnpackResponse } from "../../../../api/src/lib";
import type { ProfileController } from "../../../../api/src/profile/profile.controller";
import { NotificationContext } from "../../Notifications";
import PerformancePeriodSelector from "../../components/PerformancePeriodSelector";
import SubmitButton from "../../components/SubmitButton";
import { useFeatureToggle } from "../../lib/featureToggles";
import {
  useAuthenticatedFetch,
  useAuthenticatedMutationAsync,
} from "../../lib/api";
import Loading from "../../Loading";
import {
  PerformanceSelection,
  figurePeriodParams,
  useQueryPerformanceFigure,
} from "../performanceFigure";
import PerformanceDrivers, {
  PERFORMANCE_DRIVERS_ANCHOR,
} from "../performance-drivers/PerformanceDrivers";
import HouseholdHistoricalAumChart from "./HouseholdHistoricalAumChart";
import HouseholdHistoricalPerformanceChart from "./HouseholdHistoricalPerformanceChart";
import { HouseholdContext } from "./HouseholdInfo";
import HouseholdMembers from "./HouseholdMembers";
import HouseholdPerformance from "./HouseholdPerformance";
import HouseholdTaxSummary from "./HouseholdTaxSummary";
import SwitchToSlantWidget from "./SwitchToSlantWidget";

const HouseholdOverview = () => {
  const showPerformanceDrivers = useFeatureToggle("performance-drivers");
  const { household } = useContext(HouseholdContext);
  const { householdId } = useParams();

  const { handleSubmit } = useFormContext<EditableHousehold>();
  const [customReportPeriodParams, setCustomReportPeriodParams] = useState("");
  const [reportSelection, setReportSelection] =
    useState<PerformanceSelection>();

  // The Reports charts read over the dates of the figure for their period, as
  // the Performance panel does (R73); the selector dates when it fails.
  const { figure: reportFigure, isPending: isPendingReportFigure } =
    useQueryPerformanceFigure("households", household?.id, reportSelection);
  const reportPeriodParams = figurePeriodParams(
    reportFigure,
    customReportPeriodParams,
  );

  const { data: profileBody } =
    useAuthenticatedFetch<UnpackResponse<ProfileController["getProfile"]>>(
      "/profile",
    );
  const user = profileBody?.data;

  // Switch-to-Slant entry point (APP-1303). The card is the host placement that
  // gates SwitchToSlantWidget: it mounts for any Wealthbox-linked household whose
  // advisor has Slant connected. The switch is stateless — there is no "in
  // progress" state to gate on, so opening the card simply mounts the wizard.
  const householdIdNum = household?.id;
  const linkedToWealthbox = household?.idExternal.wealthbox != null;
  // An already-Slant-mapped household has nothing to switch — don't re-offer it.
  const notAlreadyOnSlant = household?.idExternal.slant == null;
  const slantConnected = user?.connections.slant === true;

  const showSwitchToSlant =
    typeof householdIdNum !== "undefined" &&
    linkedToWealthbox &&
    notAlreadyOnSlant &&
    slantConnected;

  const [switchWidgetOpen, setSwitchWidgetOpen] = useState(false);

  const [members, setMembers] = useState<Contact[]>([]);

  useEffect(() => {
    if (typeof household !== "undefined") {
      setMembers(household.members);
    }
  }, [household]);

  const updateHouseholdMembers = useAuthenticatedMutationAsync<
    SerializedObject<UnpackResponse<HouseholdsController["updateMembers"]>>
  >(`/v1/households/${householdId}/members`, async (members: Contact[]) => ({
    method: "PUT",
    body: JSON.stringify(members.map((contact) => contact.id)),
  }));

  const notificationContext = useContext(NotificationContext);

  const onSubmit = useMutation({
    mutationFn: async () => {
      try {
        await updateHouseholdMembers(members);
        notificationContext.pushNotification({
          id: `household-${householdId}`,
          header: "Household Updated",
          body: `${household?.name} updated`,
          variant: "success",
        });
      } catch (err) {
        console.error("Failed to save household", err);
        notificationContext.pushNotification({
          id: `household-${householdId}`,
          header: "Failed to Save Household",
          body: `${household?.name} was not saved`,
          variant: "danger",
        });
      }
    },
  });

  const reportTypes = ["AUM", "Cash", "Performance"];
  const [reportType, setReportType] = useState("AUM");

  const onChange = useCallback((ev: React.ChangeEvent<HTMLSelectElement>) => {
    setReportType(ev.currentTarget.value);
  }, []);

  return (
    <>
      <Form onSubmit={handleSubmit(() => onSubmit.mutate())}>
        <Row>
          <Col xxl={4} xl={6} className="mb-3">
            {household?.archiveDate && (
              <Row>
                <Col as="dt" sm={4}>
                  Archive Date
                </Col>
                <Col as="dd">{household.archiveDate.toLocaleDateString()}</Col>
              </Row>
            )}
            {household?.tags && (
              <Row>
                <Col as="dt" sm={4}>
                  Tags
                </Col>
                <Col as="dd">
                  {household.tags?.map((tag) => (
                    <Badge key={tag.name} bg="secondary" pill className="me-1">
                      {tag.name}
                    </Badge>
                  ))}
                </Col>
              </Row>
            )}
            <HouseholdMembers
              members={members}
              setMembers={setMembers}
              readOnly={
                !user?.connections.wealthbox &&
                !user?.connections.redtail &&
                !user?.connections.slant
              }
            />
            <SubmitButton isSubmitting={onSubmit.isPending} />
          </Col>
          <Col xl={4} className="mb-3">
            <h4>Performance</h4>
            <HouseholdPerformance useall={true} />
            {showPerformanceDrivers && (
              <a href={`#${PERFORMANCE_DRIVERS_ANCHOR}`} className="small">
                What drove this?
              </a>
            )}
          </Col>
          <Col xl={4} className="mb-3">
            <HouseholdTaxSummary useall={true} />
          </Col>
        </Row>
        {showSwitchToSlant && (
          <Row className="mb-3">
            <Col xxl={4} xl={6}>
              {switchWidgetOpen ? (
                <SwitchToSlantWidget householdId={householdIdNum} />
              ) : (
                <Card>
                  <Card.Body>
                    <Card.Title>Switch this household to Slant</Card.Title>
                    <Card.Text className="text-muted">
                      Move this Wealthbox household over to Slant. We&apos;ll
                      walk you through confirming the people, finding the
                      matching Slant household, and completing the switch.
                    </Card.Text>
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => setSwitchWidgetOpen(true)}
                    >
                      Start switch to Slant
                    </Button>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        )}
        <Row>
          <Col xxl={5} xl={6} lg={3} md={4}>
            <h5>Reports</h5>
          </Col>
          <Col xl={4} sm={4}>
            <Form.Select value={reportType} onChange={onChange}>
              {reportTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col>
            <PerformancePeriodSelector
              setCustomPeriodParams={setCustomReportPeriodParams}
              onSelectionChange={setReportSelection}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            {/* No chart read with the selector dates before the figure is in. */}
            {isPendingReportFigure && <Loading message="Chart" />}
            {!isPendingReportFigure && reportType === "AUM" && (
              <HouseholdHistoricalAumChart
                valueType="totalValue"
                customReportPeriodParams={reportPeriodParams}
              />
            )}
            {!isPendingReportFigure && reportType === "Cash" && (
              <HouseholdHistoricalAumChart
                valueType="cashValue"
                customReportPeriodParams={reportPeriodParams}
              />
            )}
            {!isPendingReportFigure && reportType === "Performance" && (
              <HouseholdHistoricalPerformanceChart
                customReportPeriodParams={reportPeriodParams}
              />
            )}
          </Col>
        </Row>
      </Form>
      {/* Outside the household form: its inputs must not submit the members
          form on Enter (the form has a submit button, so Enter submits it). */}
      {showPerformanceDrivers && typeof household !== "undefined" && (
        <PerformanceDrivers
          key={`households:${household.id}`}
          kind="households"
          id={household.id}
          name={household.name}
          accounts={household.accounts.map((account) => ({
            id: account.id,
            name: account.displayName,
          }))}
        />
      )}
    </>
  );
};

export default HouseholdOverview;
